import React, { useState, useEffect } from 'react';
import { Key, Activity, ToggleLeft, ToggleRight } from 'lucide-react';
import toast from 'react-hot-toast';
import { apiFetch, apiGet } from '../../lib/apiClient';
import { API_ROUTES } from '../../lib/apiRoutes';

export default function AdminApiKeys() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => { fetchKeys(); }, []);

  const fetchKeys = async () => {
    setLoading(true);
    try {
      setData(await apiGet(API_ROUTES.admin.apiKeys));
    } catch (e) {
      toast.error('Failed to load API keys');
    } finally {
      setLoading(false);
    }
  };

  const toggleKey = async (keyId) => {
    try {
      const res = await apiFetch(`${API_ROUTES.admin.apiKeys}/${keyId}/toggle`, {
        method: 'PATCH',
      });
      if (!res.ok) throw new Error('Failed');
      toast.success('API key toggled');
      fetchKeys();
    } catch (e) {
      toast.error('Action failed');
    }
  };

  return (
    <div>
      <div className="mb-6">
        <h1 className="text-xl font-bold text-gray-100">API Key Monitoring</h1>
        <p className="text-sm text-gray-500 mt-1">Monitor and manage platform API keys</p>
      </div>

      {/* Stats */}
      {data?.stats && (
        <div className="grid grid-cols-3 gap-4 mb-6">
          <div className="bg-gradient-to-br from-blue-400/10 to-blue-600/5 border border-dark-border rounded-xl p-5">
            <div className="flex items-center gap-2 mb-2">
              <Key size={16} className="text-blue-400" />
              <span className="text-xs text-gray-500 uppercase">Total Keys</span>
            </div>
            <p className="text-2xl font-bold text-blue-400">{data.stats.totalKeys}</p>
          </div>
          <div className="bg-gradient-to-br from-purple-400/10 to-purple-600/5 border border-dark-border rounded-xl p-5">
            <div className="flex items-center gap-2 mb-2">
              <Activity size={16} className="text-purple-400" />
              <span className="text-xs text-gray-500 uppercase">Total Requests</span>
            </div>
            <p className="text-2xl font-bold text-purple-400">{data.stats.totalRequests?.toLocaleString()}</p>
          </div>
          <div className="bg-gradient-to-br from-cyan-400/10 to-cyan-600/5 border border-dark-border rounded-xl p-5">
            <div className="flex items-center gap-2 mb-2">
              <Activity size={16} className="text-cyan-400" />
              <span className="text-xs text-gray-500 uppercase">Avg Per Key</span>
            </div>
            <p className="text-2xl font-bold text-cyan-400">{data.stats.avgRequests}</p>
          </div>
        </div>
      )}

      {/* Keys Table */}
      <div className="glass-card bg-dark-card overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-dark-border text-gray-500 text-xs uppercase tracking-wider">
                <th className="text-left px-4 py-3">Key Name</th>
                <th className="text-left px-4 py-3">Owner</th>
                <th className="text-right px-4 py-3">Requests</th>
                <th className="text-left px-4 py-3">Last Used</th>
                <th className="text-center px-4 py-3">Status</th>
                <th className="text-center px-4 py-3">Toggle</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={6} className="text-center py-12 text-gray-500">Loading...</td></tr>
              ) : !data?.keys?.length ? (
                <tr><td colSpan={6} className="text-center py-12 text-gray-500">No API keys</td></tr>
              ) : (
                data.keys.map((k) => (
                  <tr key={k.id} className="border-b border-dark-border/50 hover:bg-white/[0.02]">
                    <td className="px-4 py-3 text-gray-200 font-medium">{k.name || 'Unnamed'}</td>
                    <td className="px-4 py-3 text-gray-400 text-xs">{k.userEmail}</td>
                    <td className="px-4 py-3 text-right text-gray-300 font-mono">{(k.totalRequests || 0).toLocaleString()}</td>
                    <td className="px-4 py-3 text-gray-500 text-xs">{k.lastUsed?.split('T')[0] || '—'}</td>
                    <td className="px-4 py-3 text-center">
                      <span className={`px-2 py-0.5 rounded text-[10px] ${
                        k.isActive ? 'bg-green-500/20 text-green-400' : 'bg-red-500/20 text-red-400'
                      }`}>{k.isActive ? 'Active' : 'Disabled'}</span>
                    </td>
                    <td className="px-4 py-3 text-center">
                      <button
                        onClick={() => toggleKey(k.id)}
                        className={`p-1.5 rounded-md transition-all ${k.isActive ? 'text-green-400 hover:bg-red-500/10 hover:text-red-400' : 'text-red-400 hover:bg-green-500/10 hover:text-green-400'}`}
                        title={k.isActive ? 'Disable' : 'Enable'}
                      >
                        {k.isActive ? <ToggleRight size={18} /> : <ToggleLeft size={18} />}
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
